import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    makeStyles,
    TablePagination,
    Typography,
    useTheme,
} from '@material-ui/core'
import BusinessIcon from '@material-ui/icons/Business'
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined'
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import AddIcon from '@material-ui/icons/Add'
import {
    useAdminCompanyList,
    AdminCompany,
} from './hooks/useAdminApplicationAccess'
import { AdminManageCompanyApplicationsModal } from './AdminManageCompanyApplicationsModal'
import { SearchField } from '../../../components/SearchField'
import { Container } from '../../../components/Container'
import { GLOBAL_TOP_BAR_HEIGHT } from '../../../constants'

function formatPrice(price: string): string {
    const n = parseFloat(price)
    if (Number.isNaN(n)) return price
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(n)
}

function getCompanyAddress(company: AdminCompany): string {
    if (company.primary_address?.address1) {
        const { address1, city, state } = company.primary_address
        const parts = [address1, city, state].filter(Boolean)
        return parts.join(', ')
    }
    const parts = [company.address, company.city, company.state].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : '—'
}

/** Sum of (effective_price × billable_unit_count) for all applications; format for display. */
function getCompanyTotalMonthlyFormatted(company: AdminCompany): string {
    const applications = company.applications ?? []
    const units = company.billable_unit_count ?? 0
    const total = applications.reduce((sum, app) => {
        const n = parseFloat(app.effective_price ?? '0')
        return sum + (Number.isNaN(n) ? 0 : n) * units
    }, 0)
    return `$${total.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} per month`
}

const HEADER_HEIGHT = 56

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        padding: theme.spacing(2),
    },
    header: {
        flexShrink: 0,
        height: HEADER_HEIGHT,
        borderBottom: `1px solid ${theme.palette.divider}`,
        alignItems: 'center',
    },
    scrollArea: {
        flex: 1,
        overflow: 'auto',
        padding: theme.spacing(2, 0),
    },
    paginationContainer: {
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
}))

const CompanyCard = ({
    company,
    onManageApps,
}: {
    company: AdminCompany
    onManageApps: () => void
}) => {
    const theme = useTheme()
    const address = getCompanyAddress(company)
    const units = company.billable_unit_count ?? 0
    const applications = company.applications ?? []

    return (
        <Card
            variant="outlined"
            style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 12,
                borderLeft: `4px solid ${theme.palette.primary.light}`,
                boxShadow: theme.shadows[1],
            }}
        >
            <CardContent
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: theme.spacing(2),
                }}
            >
                {/* Name + icon */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <BusinessIcon
                        style={{
                            marginRight: theme.spacing(1),
                            marginTop: 2,
                            color: theme.palette.primary.main,
                            fontSize: 22,
                        }}
                    />
                    <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600, flex: 1 }}
                    >
                        {company.name}
                    </Typography>
                </Box>

                {/* Address */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: theme.spacing(1.5),
                    }}
                >
                    <LocationOnOutlinedIcon
                        style={{
                            marginRight: theme.spacing(1),
                            marginTop: 2,
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                        }}
                    />
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ flex: 1 }}
                    >
                        {address}
                    </Typography>
                </Box>

                {/* Units */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: theme.spacing(1, 1.5),
                        borderRadius: 8,
                        backgroundColor:
                            theme.palette.grey?.[100] ||
                            theme.palette.action.hover,
                        marginBottom: theme.spacing(1.5),
                        alignSelf: 'flex-start',
                    }}
                >
                    <PeopleOutlinedIcon
                        style={{
                            marginRight: theme.spacing(1),
                            fontSize: 20,
                            color: theme.palette.text.secondary,
                        }}
                    />
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {units}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginLeft: 4 }}
                    >
                        Units
                    </Typography>
                </Box>

                {/* Active Apps */}
                <Box style={{ marginBottom: theme.spacing(2) }}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{
                            display: 'block',
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        Active Apps
                    </Typography>
                    {applications.length > 0 ? (
                        <Box
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 6,
                            }}
                        >
                            {applications.map((app) => (
                                <Chip
                                    key={app.id}
                                    label={
                                        app.custom_price != null
                                            ? `${app.name} (${formatPrice(
                                                  app.effective_price,
                                              )} – custom)`
                                            : app.name
                                    }
                                    size="small"
                                    variant="outlined"
                                    color={
                                        app.custom_price != null
                                            ? 'secondary'
                                            : 'default'
                                    }
                                    style={{
                                        backgroundColor:
                                            theme.palette.grey?.[100] ||
                                            theme.palette.action.hover,
                                        border: 'none',
                                        fontSize: '0.75rem',
                                    }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ fontStyle: 'italic' }}
                        >
                            No apps
                        </Typography>
                    )}
                </Box>

                {/* Total per month */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: theme.spacing(1.5),
                        padding: theme.spacing(1.25, 1.5),
                        borderRadius: 8,
                        backgroundColor: theme.palette.primary.light,
                        border: `1px solid ${theme.palette.primary.main}`,
                    }}
                >
                    <AttachMoneyIcon
                        style={{
                            marginRight: theme.spacing(1),
                            color: theme.palette.primary.dark,
                            fontSize: 22,
                        }}
                    />
                    <Typography
                        variant="body1"
                        style={{
                            fontWeight: 700,
                            color: theme.palette.primary.dark,
                        }}
                    >
                        {getCompanyTotalMonthlyFormatted(company)}
                    </Typography>
                </Box>

                {/* Manage Apps button */}
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={onManageApps}
                    style={{
                        marginTop: 'auto',
                        borderRadius: 8,
                        padding: theme.spacing(1),
                        backgroundColor:
                            theme.palette.grey?.[100] ||
                            theme.palette.action.hover,
                        borderColor:
                            theme.palette.grey?.[300] || theme.palette.divider,
                        textTransform: 'none',
                        fontWeight: 500,
                    }}
                >
                    Manage Apps
                </Button>
            </CardContent>
        </Card>
    )
}

export const CompaniesAccessTab = () => {
    const classes = useStyles()
    const theme = useTheme()
    const { list, count, loading, fetch } = useAdminCompanyList()
    const [manageCompany, setManageCompany] = useState<AdminCompany | null>(
        null,
    )
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(12)

    useEffect(() => {
        fetch({
            page: page + 1,
            page_size: rowsPerPage,
            search: search.trim() || undefined,
        })
    }, [fetch, page, rowsPerPage, search])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value)
        setPage(0)
    }

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
    }

    const handleManageClose = () => {
        setManageCompany(null)
        fetch({
            page: page + 1,
            page_size: rowsPerPage,
            search: search.trim() || undefined,
        })
    }

    return (
        <div className={classes.root}>
            <Container
                style={{
                    height: HEADER_HEIGHT,
                    alignItems: 'center',
                    flexShrink: 0,
                }}
                className={classes.header}
            >
                <SearchField
                    value={search}
                    onChange={handleSearchChange}
                    label="Search companies by name or email"
                    placeholder="Search companies..."
                    iconColor={theme.palette.text.primary}
                    style={{
                        marginRight: theme.spacing(2),
                        color: theme.palette.text.primary,
                        minWidth: 280,
                    }}
                />
            </Container>

            <Box className={classes.scrollArea}>
                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={200}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {list.map((company) => (
                            <Grid
                                item
                                key={company.id}
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                            >
                                <CompanyCard
                                    company={company}
                                    onManageApps={() =>
                                        setManageCompany(company)
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            <div className={classes.paginationContainer}>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 12, 25, 50, 100]}
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </div>

            {manageCompany && (
                <AdminManageCompanyApplicationsModal
                    open={!!manageCompany}
                    company={manageCompany}
                    onClose={handleManageClose}
                />
            )}
        </div>
    )
}
